import { useMemo, useState } from 'react';
import { sortHand, tarneebBestSuit, tarneebPartnerOf, tarneebTeamOf, type TarneebLegal, type TarneebView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const isRedSuit = (s: string) => s === 'H' || s === 'D';

export function TarneebTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as TarneebView;
  const legal = useGame((s) => s.legal) as TarneebLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? tarneebTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const bidding = view.phase === 'BIDDING';
  const bidTag = (s: number) => (view.bids[s] === null ? null : view.bids[s] === 0 ? t('ta.pass') : t('ta.bidTag', { n: view.bids[s]! }));
  const playing = view.phase === 'PLAYING' || view.phase === 'TRICK_END';
  const trumpLabel = view.trump ? SUIT_SYMBOL[view.trump] : null;
  const declarerTeam = view.declarer !== null ? tarneebTeamOf(view.declarer) : null;
  const teamTricks = (team: number) => (view.tricksWon[team] ?? 0) + (view.tricksWon[team + 2] ?? 0);

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0}
      {playing && ` · ${teamTricks(team)}${declarerTeam === team && view.highBid ? `/${view.highBid.value}` : ''}`}
    </span>
  );

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[tarneebTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === tarneebPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {bidding && bidTag(s) && <span className="tag">{bidTag(s)}</span>}
                {!bidding && view.declarer === s && view.highBid && <span className="tag seen">{t('ta.bidTag', { n: view.highBid.value })}</span>}
                {playing && <span className="tag">{view.tricksWon[s] ?? 0}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {trumpLabel && (
              <>
                {' · '}
                {t('ta.trump')} <span style={{ color: isRedSuit(view.trump!) ? '#ff8a8a' : undefined, fontSize: '1.2em' }}>{trumpLabel}</span>
              </>
            )}
            {view.phase === 'TRUMP' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('ta.choosing', { name: name(turn.seats[0]) })}`}
            {bidding && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
            {teamPill(myTeam)}
            {teamPill(1 - myTeam)}
          </div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.map((r, team) => (
                  <span key={team} className={`pill ${r.delta >= 0 ? 'accent' : 'danger'}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {r.delta >= 0 ? '+' : ''}{r.delta}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat
          seat={mySeat !== null ? seats[mySeat] : undefined}
          sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts{bidTag(mySeat) && bidding ? ` · ${bidTag(mySeat)}` : ''}</> : null}
        />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && <BidPicker legal={legal} suggested={suggestedBid(view)} onBid={(v) => void act({ type: 'bid', value: v })} onPass={() => void act({ type: 'pass' })} />}
        {myTurn && legal?.kind === 'trump' && <TrumpPicker suits={legal.suits} suggested={tarneebBestSuit(view.hand)} onPick={(suit) => void act({ type: 'trump', suit })} />}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={bidding ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}</td>
                {row.map((x, team) => (
                  <td key={team} className="num" style={{ color: x.delta < 0 ? 'var(--danger)' : undefined }}>
                    {x.delta >= 0 ? '+' : ''}{x.delta}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.declarer ? `★ ${x.tricks}/${x.bid}` : x.tricks}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ta.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

/** A modest bid suggestion: nothing on a weak hand (the player can pass), otherwise the minimum or a bit above. */
function suggestedBid(view: TarneebView): number {
  const suit = tarneebBestSuit(view.hand);
  const cards = view.hand.filter((c) => c[1] === suit);
  const honours = cards.filter((c) => 'AKQJ'.includes(c[0]!)).length;
  return Math.min(13, Math.max(view.config.minBid, 5 + Math.round(cards.length / 2) + Math.floor(honours / 2)));
}

/**
 * Bid picker: chips for every bid still open, the suggestion starred, a separate confirm button so a stray tap
 * never commits a bid, and a Pass button (absent for a dealer who must bid).
 */
function BidPicker({ legal, suggested, onBid, onPass }: { legal: Extract<TarneebLegal, { kind: 'bid' }>; suggested: number; onBid: (v: number) => void; onPass: () => void }) {
  const t = useT();
  const open = Array.from({ length: Math.max(0, legal.max - legal.min + 1) }, (_, i) => legal.min + i);
  const [value, setValue] = useState(Math.min(Math.max(suggested, legal.min), legal.max));
  const [sent, setSent] = useState(false);
  const send = (fn: () => void) => {
    setSent(true);
    fn();
    setTimeout(() => setSent(false), 1500);
  };
  return (
    <div className="bid-picker" role="group" aria-label={t('ta.bid')}>
      <div className="bid-head">
        <strong>{t('ta.yourBid')}</strong>
        {legal.canPass ? <span className="dim">{t('ta.passHint')}</span> : <span className="dim">{t('ta.mustBid')}</span>}
      </div>
      {open.length > 0 && (
        <div className="bid-chips" role="radiogroup" aria-label={t('ta.bid')}>
          {open.map((v) => (
            <button key={v} role="radio" aria-checked={value === v} className={`bid-chip ${value === v ? 'on' : ''} ${v === suggested ? 'star' : ''}`} onClick={() => setValue(v)} aria-label={`${v}${v === suggested ? ` (${t('cb.suggested')})` : ''}`}>
              {v}
            </button>
          ))}
        </div>
      )}
      <div className="row" style={{ gap: 8 }}>
        {legal.canPass && (
          <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
            {t('ta.pass')}
          </button>
        )}
        {open.length > 0 && (
          <button className="btn primary grow bid-confirm" disabled={sent} onClick={() => send(() => onBid(value))}>
            {t('sp.bidValue', { n: value })}
          </button>
        )}
      </div>
    </div>
  );
}

function TrumpPicker({ suits, suggested, onPick }: { suits: string[]; suggested: string; onPick: (suit: 'S' | 'H' | 'D' | 'C') => void }) {
  const t = useT();
  const [sent, setSent] = useState(false);
  return (
    <div className="bid-picker" role="group" aria-label={t('ta.chooseTrump')}>
      <div className="bid-head">
        <strong>{t('ta.chooseTrump')}</strong>
        <span className="dim">★ {t('cb.suggested')}: <b>{SUIT_SYMBOL[suggested]}</b></span>
      </div>
      <div className="bid-chips" role="group">
        {suits.map((s) => (
          <button
            key={s}
            className={`bid-chip ${s === suggested ? 'star' : ''}`}
            style={{ color: isRedSuit(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
            disabled={sent}
            aria-label={s === 'S' ? 'Spades' : s === 'H' ? 'Hearts' : s === 'D' ? 'Diamonds' : 'Clubs'}
            onClick={() => {
              setSent(true);
              onPick(s as 'S' | 'H' | 'D' | 'C');
              setTimeout(() => setSent(false), 1500);
            }}
          >
            {SUIT_SYMBOL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
